import { Filter, TableRow } from './table-models';

export type MobileErrorKind =
    | 'NATIVE_UNCAUGHT'
    | 'JS_UNCAUGHT'
    | 'JS_UNHANDLED_REJECTION'
    | 'REACT_BOUNDARY'
    | 'HEARTBEAT_STALE'
    | 'EXPLICIT_CATCH';

export const ALL_MOBILE_ERROR_KINDS: MobileErrorKind[] = [
    'NATIVE_UNCAUGHT',
    'JS_UNCAUGHT',
    'JS_UNHANDLED_REJECTION',
    'REACT_BOUNDARY',
    'HEARTBEAT_STALE',
    'EXPLICIT_CATCH',
];

/**
 * Server-side audit row for a mobile crash. Mirrors `MobileErrorLogVO` in
 * user-service.
 */
export interface MobileError extends TableRow {
    id: number;
    clientUuid: string;

    createdAt: string;
    crashedAt?: string;
    reportedAt?: string;

    kind: MobileErrorKind;
    userId?: number;

    message?: string;
    className?: string;
    stackTrace?: string;

    lastScreen?: string;
    lastNativeCall?: string;
    breadcrumbs?: string;
    heartbeatAgeSeconds?: number;

    clientAppVersion?: string;
    deviceModel?: string;
    deviceManufacturer?: string;
    osVersion?: string;
    deviceId?: string;
    userActionAtCrash?: string;
}

export interface MobileErrorQueryParams {
    page?: number;
    itemsPerPage?: number;
    kind?: MobileErrorKind;
    filterUserId?: number;
    deviceId?: string;
    search?: string;
    fromDate?: string;
    toDate?: string;
}

export const toMobileErrorParams = (
    filter: Filter | undefined,
    page: number,
    itemsPerPage: number,
): MobileErrorQueryParams => {
    const out: MobileErrorQueryParams = { page, itemsPerPage };
    if (!filter) return out;
    if (filter.tags && filter.tags.length > 0) {
        out.kind = filter.tags[0] as MobileErrorKind;
    }
    // Filter.id carries the selected user (matches DistributorSelectFilter
    // convention). Map to filterUserId — the backend list endpoint's param.
    if (filter.id) {
        const parsed = Number(filter.id);
        if (!Number.isNaN(parsed)) out.filterUserId = parsed;
    }
    if (filter.search && filter.search.trim().length > 0) {
        out.search = filter.search.trim();
    }
    // Pad to ISO_DATE_TIME so Spring's @DateTimeFormat(ISO.DATE_TIME) parses.
    if (filter.after) out.fromDate = normalizeDate(filter.after, '00:00:00');
    if (filter.before) out.toDate = normalizeDate(filter.before, '23:59:59');
    return out;
};

const normalizeDate = (value: string, padTime: string): string => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T${padTime}`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) return value;
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${padTime}`;
    }
    return value;
};
